import { ResponseType } from 'axios';
import { toast } from 'sonner';

import { IMAGE_DETECTION_CONFIG } from '@/constants/imageDetection.const';
import {
  ORGANIZATION_TENANT_PROFILE_PICTURE_ID,
  PERSONAL_TENANT_PROFILE_PICTURE_ID,
} from '@/hooks/profilePicture';
import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { useOsintContext } from '@/state/OsintContext';
import { UseExtendQueryOptions, useMutation, useQuery } from '@/utils/api';
import { blobUrlRegistry } from '@/utils/blobUrl.util';
import { detectImageTypeSafe } from '@/utils/imageDetection.util';
import { isArrayBufferEmpty } from '@/utils/misc.util';
interface ProgressEventDetails {
  loaded: number;
  total: number;
  percent: number;
}

interface UploadFilesProps {
  name: string;
  // Blob/File preferred for large payloads — XHR sends them without
  // first materializing the bytes in JS heap. ArrayBuffer/string stay
  // supported for the small-payload callers (profile pictures, etc.).
  content: string | ArrayBuffer | Blob;
  ignoreSnackbar?: boolean;
  onProgress?: (progressEvent: ProgressEventDetails) => void;
  praetorian?: boolean;
  signal?: AbortSignal;
  // Optional retention. Allowed values are fixed by the backend enum
  // (FILE_TTL_OPTIONS): the S3 object is tagged for lifecycle expiry and
  // the file record gets a matching DynamoDB TTL. Omit for no expiry.
  ttl?: FileTtl;
}

// Mirrors ttlDurations in backend/pkg/handler/handlers/file/file.go — each
// value needs a matching tag-filtered lifecycle rule on the bucket.
export const FILE_TTL_OPTIONS = ['7d', '14d', '30d', '90d'] as const;
export type FileTtl = (typeof FILE_TTL_OPTIONS)[number];

/**
 * Upload file with automatic toast.promise for loading/success/error states
 *
 * Default behavior: Shows toast.promise with loading/success/error states
 * Override: Pass { ignoreSnackbar: true } to suppress all toasts
 *
 * Uses toast.promise pattern for long-running operations with built-in loading states.
 * This is the recommended pattern for file uploads, asset updates, and other operations
 * that take >1 second to complete.
 *
 * @example
 * // Standard usage (automatic loading + success toasts)
 * const { mutate: upload } = useUploadFile();
 * upload({ name: 'file.pdf', content: arrayBuffer });
 *
 * @example
 * // Silent upload (no toast notifications)
 * upload({ name: 'file.pdf', content: arrayBuffer, ignoreSnackbar: true });
 */
export function useUploadFile(email?: string) {
  const { me } = useAuth();
  const axios = useAxios({ email });

  const { invalidate: invalidateMyFiles } = useMy(
    { resource: 'file' },
    { enabled: false }
  );

  const invalidateProfilePicture = () => {
    queryClient.invalidateQueries({
      predicate: query => {
        const baseKey = getQueryKey.getGavatarProfilePicture(email || me);
        return (
          query.queryKey.length >= baseKey.length &&
          baseKey.every((key, index) => query.queryKey[index] === key)
        );
      },
    });
  };

  return useMutation({
    defaultErrorMessage: 'Failed to Upload file',
    mutationFn: async (props: UploadFilesProps) => {
      const contentSize =
        props.content instanceof Blob
          ? props.content.size
          : props.content instanceof ArrayBuffer
            ? props.content.byteLength
            : props.content.length;

      const name =
        props.name === PERSONAL_TENANT_PROFILE_PICTURE_ID
          ? 'Profile Picture'
          : props.name === ORGANIZATION_TENANT_PROFILE_PICTURE_ID
            ? 'Organization Logo'
            : props.name;

      // Use multipart upload for files larger than 2GB. Multipart still
      // requires ArrayBuffer for the chunked path; Blob/File callers that
      // exceed 2GB must convert. The single-upload path below handles
      // Blob/File natively without buffering.
      if (
        props.content instanceof ArrayBuffer &&
        contentSize > MAX_SINGLE_UPLOAD_SIZE
      ) {
        const uploadPromise = uploadFileMultipart({
          fileName: props.name,
          fileContent: props.content,
          onProgress: props.onProgress,
          axios,
          praetorian: props.praetorian,
          ttl: props.ttl,
        });

        if (!props.ignoreSnackbar) {
          toast.promise(uploadPromise, {
            loading: `Uploading ${name} (multipart)...`,
            success: `${name} uploaded`,
            error: `Failed to upload ${props.name}`,
          });
        }
        await uploadPromise;
        return null;
      }

      // Standard single upload for files under 2GB
      const res = await axios.put(`/file`, null, {
        params: {
          name: props.name,
          ...(props.praetorian ? { praetorian: 'true' } : {}),
          ...(props.ttl ? { ttl: props.ttl } : {}),
        },
      });

      const uploadUrl = res.data.url;

      if (!uploadUrl) {
        throw new Error('Failed to upload file');
      }

      const uploadPromise = uploadFileToSignedURL(
        uploadUrl,
        props.content,
        props.onProgress,
        props.signal,
        // TTL'd uploads return signed headers (x-amz-tagging) that must be
        // echoed on the PUT or S3 rejects the signature.
        res.data.headers
      );

      if (!props.ignoreSnackbar) {
        toast.promise(uploadPromise, {
          loading: `Uploading ${name}...`,
          success: `${name} uploaded`,
          error: `Failed to upload ${props.name}`,
        });
      }
      await uploadPromise;
      return null;
    },
    onSuccess: (_, variable) => {
      invalidateMyFiles();

      queryClient.invalidateQueries({
        queryKey: getQueryKey.getFile({ name: variable.name }),
      });

      // Always invalidate profile picture when it's either profile picture being uploaded
      if (
        variable.name === PERSONAL_TENANT_PROFILE_PICTURE_ID ||
        variable.name === ORGANIZATION_TENANT_PROFILE_PICTURE_ID
      ) {
        invalidateProfilePicture();
      }
    },
  });
}

export function useDeleteFile(email?: string) {
  const axios = useAxios({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to Delete file',
    mutationFn: async (props: { name: string; praetorian?: boolean }) => {
      await axios.delete(`/file`, {
        params: {
          name: props.name,
          ...(props.praetorian ? { praetorian: 'true' } : {}),
        },
      });
      return null;
    },
  });
}

// 2GB limit for browser XHR ArrayBuffer uploads
const MAX_SINGLE_UPLOAD_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
const MULTIPART_CHUNK_SIZE = 100 * 1024 * 1024; // 100MB per chunk

export const uploadFileToSignedURL = async (
  uploadUrl: string,
  content: string | ArrayBuffer | Blob,
  onProgress?: (progressEvent: ProgressEventDetails) => void,
  signal?: AbortSignal,
  headers?: Record<string, string>
): Promise<void> => {
  const contentSize =
    content instanceof Blob
      ? content.size
      : content instanceof ArrayBuffer
        ? content.byteLength
        : content.length;

  // For large files, we can't use simple XHR due to browser limitations
  if (
    !(content instanceof Blob) &&
    content instanceof ArrayBuffer &&
    contentSize > MAX_SINGLE_UPLOAD_SIZE
  ) {
    throw new Error(
      `File size (${(contentSize / 1024 / 1024 / 1024).toFixed(2)}GB) exceeds browser upload limit of 2GB. Please use multipart upload.`
    );
  }

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Set timeout for large files (2 hours)
    xhr.timeout = 2 * 60 * 60 * 1000;

    xhr.open('PUT', uploadUrl, true);

    // Attach the progress event handler
    xhr.upload.onprogress = (event: ProgressEvent<EventTarget>) => {
      if (onProgress && event.lengthComputable) {
        const progress: ProgressEventDetails = {
          loaded: event.loaded,
          total: event.total,
          percent: (event.loaded / event.total) * 100,
        };
        onProgress(progress);
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200 || xhr.status === 204) {
        resolve();
      } else {
        reject(
          new Error(`Failed to upload file: ${xhr.status} ${xhr.statusText}`)
        );
      }
    };

    xhr.onerror = () => {
      reject(new Error(`Upload failed: Network error`));
    };

    xhr.onabort = () => {
      reject(new Error('Upload was aborted'));
    };

    xhr.ontimeout = () => {
      reject(new Error('Upload timed out'));
    };

    // Set appropriate Content-Type based on content
    if (content instanceof Blob && content.type) {
      xhr.setRequestHeader('Content-Type', content.type);
    } else if (content instanceof ArrayBuffer || content instanceof Blob) {
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    }

    // Headers the presigner signed into the URL (e.g. x-amz-tagging for
    // TTL'd uploads) — required verbatim for the signature to validate.
    Object.entries(headers ?? {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    try {
      // XHR.send accepts Blob/File natively without buffering the
      // contents into JS heap — the browser streams the upload.
      xhr.send(content);
    } catch (error) {
      reject(error);
    }
  });
};

interface MultipartUploadOptions {
  fileName: string;
  fileContent: ArrayBuffer;
  onProgress?: (progressEvent: ProgressEventDetails) => void;
  axios: ReturnType<typeof useAxios>;
  praetorian?: boolean;
  ttl?: FileTtl;
}

// Multipart upload for files larger than 2GB
export const uploadFileMultipart = async ({
  fileName,
  fileContent,
  onProgress,
  axios,
  praetorian,
  ttl,
}: MultipartUploadOptions): Promise<void> => {
  // Step 1: Create multipart upload. The ttl tags the completed object
  // server-side; complete derives the file record's TTL from that tag,
  // so ttl is only ever sent here.
  const createRes = await axios.post(`/file/multipart/create`, null, {
    params: {
      name: fileName,
      ...(praetorian ? { praetorian: 'true' } : {}),
      ...(ttl ? { ttl } : {}),
    },
  });
  const uploadId = createRes.data.uploadId;

  if (!uploadId) {
    throw new Error('Failed to create multipart upload: no uploadId returned');
  }

  // Step 2: Upload parts in chunks
  const numParts = Math.ceil(fileContent.byteLength / MULTIPART_CHUNK_SIZE);
  const completedParts: Array<{ partNumber: number; etag: string }> = [];
  let uploadedBytes = 0;

  try {
    for (let partNumber = 1; partNumber <= numParts; partNumber++) {
      const start = (partNumber - 1) * MULTIPART_CHUNK_SIZE;
      const end = Math.min(
        start + MULTIPART_CHUNK_SIZE,
        fileContent.byteLength
      );
      const chunk = fileContent.slice(start, end);

      // Get presigned URL for this part
      const partRes = await axios.get(`/file/multipart/part`, {
        params: {
          name: fileName,
          uploadId: uploadId,
          partNumber: partNumber,
          ...(praetorian ? { praetorian: 'true' } : {}),
        },
      });
      const partUrl = partRes.data.url;

      if (!partUrl) {
        throw new Error(`Failed to get presigned URL for part ${partNumber}`);
      }

      // Upload this part
      const uploadResponse = await fetch(partUrl, {
        method: 'PUT',
        body: chunk,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Part upload failed: ${uploadResponse.status}`);
      }

      // Get ETag from response headers
      const etag =
        uploadResponse.headers.get('ETag') ||
        uploadResponse.headers.get('etag');

      if (!etag) {
        throw new Error(
          'No ETag returned from upload - this may be a CORS configuration issue'
        );
      }

      const cleanETag = etag.replace(/"/g, ''); // Remove quotes from ETag
      completedParts.push({ partNumber, etag: cleanETag });
      uploadedBytes += chunk.byteLength;

      // Update progress
      if (onProgress) {
        const progress: ProgressEventDetails = {
          loaded: uploadedBytes,
          total: fileContent.byteLength,
          percent: (uploadedBytes / fileContent.byteLength) * 100,
        };
        onProgress(progress);
      }
    }

    // Step 3: Complete multipart upload
    await axios.post(
      `/file/multipart/complete`,
      {
        name: fileName,
        uploadId: uploadId,
        parts: completedParts,
      },
      {
        params: { ...(praetorian ? { praetorian: 'true' } : {}) },
      }
    );
  } catch (error) {
    // Abort the multipart upload to free S3 storage
    try {
      await axios.post(
        `/file/multipart/abort`,
        {
          name: fileName,
          uploadId: uploadId,
        },
        {
          params: { ...(praetorian ? { praetorian: 'true' } : {}) },
        }
      );
    } catch {
      // Best-effort abort — don't mask the original error
    }
    throw error;
  }
};
interface DownloadFilesProps {
  name: string;
  downloadName?: string;
  email?: string;
  type?: string;
  responseType?: ResponseType;
  openInNewTab?: boolean;
  praetorian?: boolean;
}

export function useDownloadFile(email?: string) {
  const axios = useAxios({ email });

  return useMutation({
    defaultErrorMessage: `Failed to download file`,
    mutationFn: async (props: DownloadFilesProps) => {
      const getFileName = (path: string): string => {
        const lastPart = path.split('/').pop() || path;
        return lastPart.split(':')[0];
      };

      // If props.email is provided, override the account header for this specific request
      const headers = props.email ? { account: props.email } : undefined;

      const promise = (async () => {
        // Get presigned URL from backend
        const res = await axios.get<{ url: string }>(`/file`, {
          ...(headers && { headers }),
          params: {
            name: props.name,
            ...(props.praetorian ? { praetorian: 'true' } : {}),
          },
        });

        const presignedUrl = res.data.url;

        if (!presignedUrl) {
          throw new Error('No download URL returned');
        }

        // Use native browser download (no Authorization header, no memory loading)
        const a = document.createElement('a');
        a.href = presignedUrl;
        a.download =
          props.downloadName || getFileName(props.name) || props.name;
        if (props.openInNewTab) {
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
        }
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      })();

      toast.promise(promise, {
        loading: `Downloading ${props.downloadName || props.name}...`,
        success: `${props.downloadName || props.name} downloaded`,
        error: `Failed to download ${props.downloadName || props.name}`,
      });

      await promise;
    },
  });
}

export function useOpenFile(email?: string) {
  const axios = useAxios({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to load file',
    mutationFn: async (props: DownloadFilesProps) => {
      try {
        // Step 1: Get presigned URL from backend
        const res = await axios.get<{ url: string }>(`/file`, {
          params: {
            name: props.name,
            ...(props.praetorian ? { praetorian: 'true' } : {}),
          },
        });

        const presignedUrl = res.data.url;

        if (!presignedUrl) {
          throw new Error('No download URL returned');
        }

        // Step 2: Fetch file data from presigned URL
        const fileResponse = await fetch(presignedUrl);

        if (!fileResponse.ok) {
          throw new Error('Failed to fetch file from presigned URL');
        }

        return await fileResponse.blob();
      } catch {
        // Silently handle error
      }
    },
  });
}

export interface GetFilesProps {
  name?: string;
  global?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useGetFile<T = any>(
  props: GetFilesProps,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseExtendQueryOptions<any> & {
    responseType?: ResponseType;
    getBlobType?: string;
    doNotImpersonate?: boolean;
    email?: string;
  }
) {
  const { isOsint } = useOsintContext();
  const axios = useAxios({
    doNotImpersonate: options?.doNotImpersonate,
    email: options?.email,
  });

  return useQuery<T>({
    ...options,
    defaultErrorMessage: `Failed to get file`,
    retry: 0,
    queryKey: [
      ...getQueryKey.getFile(props),
      ...(options?.email ? [options.email] : []),
      ...(isOsint ? ['osint'] : []),
    ],
    queryFn: async () => {
      try {
        // Step 1: Get presigned URL from backend
        const res = await axios.get<{ url: string }>(`/file`, {
          params: {
            name: props.name,
            ...(props.global !== undefined && { global: props.global }),
            ...(isOsint ? { osint: 'true' } : {}),
          },
        });

        const presignedUrl = res.data.url;

        if (!presignedUrl) {
          return '' as T;
        }

        // Step 2: Fetch file data from presigned URL with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(
          () => controller.abort(),
          IMAGE_DETECTION_CONFIG.FETCH_TIMEOUT_MS
        );

        try {
          const fileResponse = await fetch(presignedUrl, {
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!fileResponse.ok) {
            return '' as T;
          }

          // Step 3: Use optimized image detection (multi-tier strategy)
          // Only attempt detection if no explicit responseType is set
          if (!options?.responseType && !options?.getBlobType) {
            const detection = await detectImageTypeSafe(
              fileResponse,
              props.name,
              presignedUrl
            );

            if (detection.isImage && detection.mimeType) {
              const blob = await fileResponse.blob();
              if (blob) {
                const blobUrl = URL.createObjectURL(
                  new Blob([blob], { type: detection.mimeType })
                );

                // Register blob URL for automatic cleanup
                blobUrlRegistry.register(blobUrl, props.name);

                return blobUrl as T;
              }
            }
          }

          // Step 4: Handle different response types
          const contentType = fileResponse.headers.get('content-type') || '';

          if (options?.getBlobType) {
            if (options?.responseType === 'arraybuffer') {
              const arrayBuffer = await fileResponse.arrayBuffer();
              if (!isArrayBufferEmpty(arrayBuffer || '""')) {
                const blobUrl = URL.createObjectURL(
                  new Blob([arrayBuffer], { type: options?.getBlobType })
                );
                blobUrlRegistry.register(blobUrl, props.name);
                return blobUrl as T;
              }
            } else {
              const blob = await fileResponse.blob();
              if (blob) {
                const blobUrl = URL.createObjectURL(
                  new Blob([blob], { type: options?.getBlobType })
                );
                blobUrlRegistry.register(blobUrl, props.name);
                return blobUrl as T;
              }
            }
          } else {
            // Return raw data based on responseType
            if (options?.responseType === 'arraybuffer') {
              return (await fileResponse.arrayBuffer()) as T;
            } else if (options?.responseType === 'blob') {
              return (await fileResponse.blob()) as T;
            } else {
              // Automatically parse JSON responses based on content-type or content structure
              const text = await fileResponse.text();

              // Check if content looks like JSON (starts with { or [)
              const looksLikeJSON =
                text &&
                (text.trimStart().startsWith('{') ||
                  text.trimStart().startsWith('['));
              const isJSONContentType =
                contentType?.includes('application/json');

              // Try to parse as JSON if content-type indicates JSON OR if content looks like JSON
              if ((isJSONContentType || looksLikeJSON) && text) {
                try {
                  return JSON.parse(text) as T;
                } catch {
                  // If JSON parsing fails, return as text (backward compatible)
                  return text as T;
                }
              }

              return text as T;
            }
          }

          return '' as T;
        } catch (fetchError) {
          // Handle fetch timeout
          if (fetchError instanceof Error && fetchError.name === 'AbortError') {
            console.error('File fetch timeout:', props.name);
            toast.error('File download timed out. Please try again.');
          }
          throw fetchError;
        }
      } catch (error) {
        console.error('useGetFile error:', error, { fileName: props.name });
        return '' as T;
      }
    },
    enabled: options?.enabled ?? Boolean(props.name),
  });
}
